import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import { deleteWithBodyAuthorized } from "@/lib/fetch";

export function useBulkDelete({
  client,
  table_name,
}: {
  client: string;
  table_name: string;
}) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [isOpen, setIsOpen] = useState(false);
  const [pendingIds, setPendingIds] = useState<number[]>([]);
  const clearSelectionRef = useRef<(() => void) | null>(null);

  const mutation = useMutation({
    mutationFn: (ids: number[]) =>
      deleteWithBodyAuthorized(
        `${import.meta.env.VITE_BACKEND_URL}/${client}/${table_name}/bulk`,
        { ids },
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [client, table_name] });
      toast.success(t("form.toast.delete_success"));
      setIsOpen(false);
      setPendingIds([]);
      clearSelectionRef.current?.();
      clearSelectionRef.current = null;
    },
    onError: () => {
      toast.error(t("form.error.base_delete"));
    },
  });

  const openConfirm = (ids: number[], clearSelection: () => void) => {
    clearSelectionRef.current = clearSelection;
    setPendingIds(ids);
    setIsOpen(true);
  };

  const confirmDelete = () => {
    if (pendingIds.length === 0) return;
    mutation.mutate(pendingIds);
  };

  const cancelDelete = () => {
    setIsOpen(false);
    setPendingIds([]);
    clearSelectionRef.current = null;
  };

  return {
    openConfirm,
    confirmDelete,
    cancelDelete,
    isOpen,
    isLoading: mutation.isPending,
    count: pendingIds.length,
  };
}
